export type SearchGate = 'canManageBilling' | 'canManageTeamProfiles' | 'isSuperAdmin';

export type SearchAction = 'invite-network' | 'day-prep' | 'switch-office' | 'support';

export interface SearchEntry {
  id: string;
  title: string;
  group: string;
  kind: 'place' | 'howto';
  /** One or two lines shown under a how-to, and used for matching. */
  hint?: string;
  /** Button label after "Open", for example "Add a patient". */
  goLabel?: string;
  aliases?: string[];
  /** App path. Office id is filled in for paths that contain :officeId. */
  href?: string;
  action?: SearchAction;
  gate?: SearchGate;
  /** Shown when the query is empty. */
  featured?: boolean;
}

export interface SearchAccess {
  mainOfficeId?: string;
  canManageBilling?: boolean;
  canManageTeamProfiles?: boolean;
  isSuperAdmin?: boolean;
}

const EMBED_STEPS: Array<{ slug: string; title: string; aliases?: string[] }> = [
  {
    slug: 'welcome',
    title: 'Welcome message',
    aliases: ['embed form welcome message', 'greeting on the referral form', 'first impression'],
  },
  {
    slug: 'specialties',
    title: 'Specialties',
    aliases: ['embed form specialties', 'which specialties the form shows'],
  },
  {
    slug: 'scope',
    title: 'Scope',
    aliases: ['embed form scope', 'what treatment the form covers'],
  },
  {
    slug: 'skills',
    title: 'Skills',
    aliases: ['embed form skills', 'clinical skills on the form'],
  },
  {
    slug: 'experience',
    title: 'Experience',
    aliases: ['embed form experience', 'years of experience on the form'],
  },
  {
    slug: 'insurance',
    title: 'Insurance',
    aliases: ['embed form insurance', 'plans accepted on the website form'],
  },
  {
    slug: 'referral-reasons',
    title: 'Referral reasons',
    aliases: ['embed form referral reasons', 'why refer a patient'],
  },
  {
    slug: 'xray',
    title: 'X-rays',
    aliases: ['embed form x-rays', 'upload radiographs on the referral form'],
  },
  {
    slug: 'routing',
    title: 'Routing',
    aliases: ['embed form routing', 'which office receives the form'],
  },
  {
    slug: 'optional-settings',
    title: 'Optional settings',
    aliases: ['embed form optional settings', 'extra website form options'],
  },
  {
    slug: 'install',
    title: 'Install the form',
    aliases: ['copy the embed snippet', 'embed code for the website', 'install the website widget'],
  },
  {
    slug: 'general-dentists',
    title: 'Contact Us form',
    aliases: ['general dentist contact us form', 'gd website form'],
  },
];

export const SEARCH_CATALOG: SearchEntry[] = [
  {
    id: 'analytics',
    title: 'Analytics',
    group: 'Analytics',
    kind: 'place',
    href: '/analytics',
    aliases: ['referral analytics dashboard', 'referral volume', 'ghost rate'],
  },
  {
    id: 'incoming',
    title: 'Incoming Referrals',
    group: 'Referrals',
    kind: 'place',
    href: '/referrals/incoming',
    aliases: ['received referrals', 'referrals sent to my office', 'incoming referral list'],
    featured: true,
  },
  {
    id: 'outgoing',
    title: 'Outgoing Referrals',
    group: 'Referrals',
    kind: 'place',
    href: '/referrals/outgoing',
    aliases: ['sent referrals', 'referrals I sent to another office', 'outgoing referral list'],
  },
  {
    id: 'new-referral',
    title: 'New Referral',
    group: 'Referrals',
    kind: 'place',
    href: '/referrals/new',
    aliases: ['send a new referral', 'create a referral', 'refer a patient to another office'],
    featured: true,
  },
  {
    id: 'add-patient',
    title: 'Add a patient',
    group: 'Referrals',
    kind: 'place',
    href: '/referrals/add-patient',
    aliases: ['new patient', 'add a patient record', 'create a patient before referring'],
    featured: true,
  },
  {
    id: 'howto-add-patient',
    title: 'How to add a patient',
    group: 'Referrals',
    kind: 'howto',
    hint: 'Open Add a patient and enter the patient’s name, date of birth, and contact details.',
    href: '/referrals/add-patient',
    goLabel: 'Add a patient',
    aliases: ['create a patient', 'register a new patient', 'enter patient name and date of birth'],
    featured: true,
  },
  {
    id: 'howto-forward',
    title: 'How to forward a referral',
    group: 'Referrals',
    kind: 'howto',
    hint: 'Open an incoming referral. In Status & Schedule, change the status to Referred Out and choose Update Status. Forward Referral then appears under the status dropdown.',
    href: '/referrals/incoming',
    goLabel: 'Incoming Referrals',
    aliases: [
      'forward a referral',
      'send to another doctor',
      'referred out',
      'update status to referred out',
      'forward referral button under the status dropdown',
    ],
    featured: true,
  },
  {
    id: 'howto-new-referral',
    title: 'How to send a new referral',
    group: 'Referrals',
    kind: 'howto',
    hint: 'Choose New Referral, pick the receiving office, and add the patient and reason.',
    href: '/referrals/new',
    goLabel: 'New Referral',
    aliases: ['create a referral', 'refer a patient', 'send a patient to a receiving office'],
  },
  {
    id: 'leads',
    title: 'Contact Us Leads',
    group: 'Contact Us Leads',
    kind: 'place',
    href: '/leads',
    aliases: ['website contact us leads', 'patient inquiries from the website form'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    group: 'Notifications',
    kind: 'place',
    href: '/notifications',
    aliases: ['alerts', 'unread notifications', 'messages about referrals'],
  },
  {
    id: 'notes',
    title: 'Notes',
    group: 'Notes',
    kind: 'place',
    href: '/notes',
    aliases: ['administrative notes', 'internal notes', 'clinical notes', 'notes on a referral'],
  },
  {
    id: 'company',
    title: 'Company Settings',
    group: 'Settings',
    kind: 'place',
    href: '/company-settings',
    aliases: [
      'practice profile',
      'organization',
      'company name and logo',
      'email notifications',
      'analytics report',
    ],
  },
  {
    id: 'offices',
    title: 'Offices',
    group: 'Offices',
    kind: 'place',
    href: '/offices',
    aliases: ['office locations', 'switch between offices', 'all of my locations'],
  },
  {
    id: 'office-details',
    title: 'Office details',
    group: 'Offices',
    kind: 'place',
    href: '/offices/:officeId?standalone=1',
    aliases: ['office profile', 'office address and hours', 'edit this office'],
  },
  {
    id: 'embed',
    title: 'Website Lead Forms',
    group: 'Website Lead Forms',
    kind: 'place',
    href: '/offices/:officeId/embed?standalone=1',
    aliases: [
      'embed form',
      'embed',
      'embed the referral form',
      'website form',
      'website widget',
      'put the referral form on my website',
    ],
    featured: true,
  },
  {
    id: 'howto-embed',
    title: 'How to embed the referral form',
    group: 'Website Lead Forms',
    kind: 'howto',
    hint: 'Open Website Lead Forms, finish the steps, then copy the snippet from Install the form onto your website.',
    href: '/offices/:officeId/embed/install?standalone=1',
    goLabel: 'Install the form',
    aliases: [
      'install the form on my website',
      'copy the embed code snippet',
      'website widget install',
    ],
    featured: true,
  },
  {
    id: 'howto-known-for',
    title: 'How to edit "What we are known for"',
    group: 'Web Form Edits',
    kind: 'howto',
    hint: 'Open Offices, then Website Lead Forms, and open the embed form. Scroll to What we are known for. Add or delete a skill or service, then click Save. The web form updates in real time.',
    href: '/offices/:officeId/embed/referral?standalone=1',
    goLabel: 'Website Lead Forms',
    aliases: [
      'what we are known for',
      'what you are known for',
      'edit skills and services on the web form',
      'add or delete a skill on the embed form',
      'web form edits',
    ],
  },
  ...EMBED_STEPS.map(
    (step): SearchEntry => ({
      id: `embed-${step.slug}`,
      title: step.title,
      group: 'Website Lead Forms',
      kind: 'place',
      href: `/offices/:officeId/embed/${step.slug}?standalone=1`,
      aliases: step.aliases,
    })
  ),
  {
    id: 'referral-access',
    title: 'Referral Access Code',
    group: 'Settings',
    kind: 'place',
    href: '/company-settings/referral-access',
    aliases: [
      'who can refer to this office',
      'referral access pin',
      'company access pin',
      'let another office send referrals',
    ],
  },
  {
    id: 'team',
    title: 'Dental Team',
    group: 'Dental Team',
    kind: 'place',
    href: '/team',
    aliases: ['team', 'dental team', 'office staff', 'team members', 'users in this office'],
  },
  {
    id: 'roles',
    title: 'Team member roles',
    group: 'Dental Team',
    kind: 'place',
    href: '/team?officeId=:officeId',
    aliases: ['team permissions', 'team member roles', 'who can manage the office'],
    gate: 'canManageTeamProfiles',
  },
  {
    id: 'howto-invite-team',
    title: 'How to invite a team member',
    group: 'Dental Team',
    kind: 'howto',
    hint: 'Open Dental Team and choose Invite team member. They get an email to join this office.',
    href: '/team',
    goLabel: 'Dental Team',
    aliases: [
      'add a staff member',
      'invite an employee',
      'invite a user to this office',
      'create an account for a team member',
    ],
  },
  {
    id: 'invite-network',
    title: 'Invite your Network',
    group: 'Network',
    kind: 'place',
    action: 'invite-network',
    aliases: [
      'invite a provider',
      'invite a doctor',
      'invite a dentist to the network',
      'invite another practice',
    ],
    featured: true,
  },
  {
    id: 'howto-invite-provider',
    title: 'How to invite another provider',
    group: 'Network',
    kind: 'howto',
    hint: 'Choose Invite your Network in the header and send the invite to the other practice.',
    action: 'invite-network',
    goLabel: 'Invite your Network',
    aliases: [
      'invite a doctor',
      'invite a specialist',
      'invite a general dentist',
      'add another provider to my network',
    ],
    featured: true,
  },
  {
    id: 'memberships',
    title: 'Memberships',
    group: 'Memberships',
    kind: 'place',
    href: '/membership-plans',
    aliases: ['membership plans', 'in-house membership plan', 'patient membership plans'],
  },
  {
    id: 'billing',
    title: 'Billing',
    group: 'Billing',
    kind: 'place',
    href: '/billing',
    aliases: ['subscription and invoices', 'payment method', 'manage billing for this office'],
    gate: 'canManageBilling',
  },
  {
    id: 'settings',
    title: 'My Settings',
    group: 'Settings',
    kind: 'place',
    href: '/company-settings/my-settings',
    aliases: ['my profile', 'change my password', 'account settings'],
  },
  {
    id: 'day-prep',
    title: "Provider's Day-Prep Station",
    group: 'Day-Prep',
    kind: 'place',
    action: 'day-prep',
    aliases: [
      'day prep station',
      'daily prep before the procedure',
      'pre-op day viewer',
      'patient and referring info for the scheduled day',
    ],
  },
  {
    id: 'admin',
    title: 'Admin',
    group: 'Admin',
    kind: 'place',
    href: '/ops/console',
    aliases: ['admin console', 'super admin tools'],
    gate: 'isSuperAdmin',
  },
  {
    id: 'howto-day-prep',
    title: 'How to see a scheduled referral in Day-Prep',
    group: 'Day-Prep',
    kind: 'howto',
    hint: 'Open the incoming referral and set Status & Schedule to Scheduled. Check Add to Pre-Op Day Viewer, then Schedule. Open Provider’s Day-Prep Station, pick that day, and the case shows under Patient & Referring Info.',
    href: '/referrals/incoming',
    goLabel: 'Incoming Referrals',
    aliases: [
      'day prep for a scheduled referral',
      'set status and schedule to scheduled',
      'add to pre-op day viewer',
      'procedure day is booked',
      'see patient and referring info on the scheduled day',
    ],
    featured: true,
  },
  {
    id: 'howto-support',
    title: 'How to open support chat',
    group: 'Support',
    kind: 'howto',
    hint: 'Support chat is the button at the bottom right of the screen. Open it to message the ToothReferral team.',
    action: 'support',
    goLabel: 'support chat',
    aliases: [
      'open the support chat',
      'help and support',
      'contact support',
      'support button at the bottom right',
      'live chat with toothreferral',
      'message the support team',
    ],
    featured: true,
  },
  {
    id: 'howto-other-office',
    title: 'How to find a patient at another office',
    group: 'Offices',
    kind: 'howto',
    hint: 'Patients and referrals belong to one office. Lists only show the office selected in the header. Switch offices, then look again.',
    action: 'switch-office',
    goLabel: 'the office menu',
    aliases: [
      "can't find a patient",
      'cannot find a patient at this office',
      'patient is at another location',
      'wrong office selected',
      'switch the office in the header',
      'missing patient in the current location',
    ],
  },
];

export interface RankedSearchEntry extends SearchEntry {
  hrefResolved?: string;
}

function gatesAllow(entry: SearchEntry, access: SearchAccess): boolean {
  if (!entry.gate) return true;
  if (entry.gate === 'canManageBilling') return !!access.canManageBilling;
  if (entry.gate === 'canManageTeamProfiles') return !!access.canManageTeamProfiles;
  return !!access.isSuperAdmin;
}

function resolveHref(href: string | undefined, officeId: string | undefined): string | undefined {
  if (!href) return undefined;
  if (!href.includes(':officeId')) return href;
  if (!officeId) return undefined;
  return href.replaceAll(':officeId', officeId);
}

export const SEARCH_SUGGESTIONS = [
  'How do I embed the form?',
  'Forward a referral',
  'Add a patient',
  'Invite a provider',
  "Can't find a patient?",
  'Day-Prep for a scheduled referral',
  'Open support chat',
];

export function answerHeading(title: string): string {
  const stripped = title.replace(/^How to /i, '');
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

export function officeSearchHint(officeName: string | undefined, officeCount: number): string {
  if (officeCount <= 1) {
    return 'Patients and referrals belong to this office. This account has one location, so a missing patient was not added under another office.';
  }
  const name = officeName?.trim() || 'the office selected in the header';
  return `Patients and referrals belong to one office. You are in ${name}, so lists only show that location. Open the office menu in the header and switch to the office where the patient was added, then look again.`;
}

function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Higher is better. 0 means no match. Subsequence hits stay sensitive to partial typing. */
export function matchScore(query: string, text: string): number {
  const q = normalize(query);
  const t = normalize(text);
  if (!q || !t) return 0;
  if (t === q) return 100;
  if (t.startsWith(q)) return 86;
  if (t.includes(q)) return 72;

  const words = t.split(' ');
  if (words.some(word => word.startsWith(q))) return 68;

  let qi = 0;
  let consecutive = 0;
  let bestRun = 0;
  let hits = 0;
  for (const ch of t) {
    if (ch === ' ') {
      consecutive = 0;
      continue;
    }
    if (ch === q[qi]) {
      qi += 1;
      consecutive += 1;
      hits += 1;
      bestRun = Math.max(bestRun, consecutive);
      if (qi === q.length) {
        return 28 + bestRun * 4 + Math.min(hits, 8);
      }
    } else {
      consecutive = 0;
    }
  }
  return 0;
}

function tokenScore(token: string, entry: SearchEntry): number {
  const fields: Array<[string, number]> = [
    [entry.title, 1],
    [entry.group, 0.72],
    ...(entry.aliases ?? []).map(alias => [alias, 0.95] as [string, number]),
    ...(entry.hint ? [[entry.hint, 0.55] as [string, number]] : []),
  ];
  return fields.reduce(
    (best, [text, weight]) => Math.max(best, matchScore(token, text) * weight),
    0
  );
}

function entryScore(query: string, entry: SearchEntry): number {
  const tokens = meaningfulTokens(query);
  if (tokens.length === 0) return 0;
  let total = 0;
  for (const token of tokens) {
    const score = tokenScore(token, entry);
    if (score === 0) return 0;
    total += score;
  }
  const titleCoversQuery = tokens.every(token => matchScore(token, entry.title) > 0);
  const oneAliasCoversQuery = (entry.aliases ?? []).some(alias =>
    tokens.every(token => matchScore(token, alias) > 0)
  );
  const togetherBonus = (titleCoversQuery ? 25 : 0) + (oneAliasCoversQuery ? 15 : 0);
  return total / tokens.length + togetherBonus;
}

/** Question words and one-letter leftovers ("can't" → "t") should not hide a match. */
const QUERY_STOP_WORDS = new Set([
  'a',
  'an',
  'the',
  'to',
  'of',
  'in',
  'on',
  'for',
  'and',
  'or',
  'how',
  'do',
  'does',
  'i',
  'me',
  'my',
  'is',
  'it',
  'can',
  'please',
  'where',
  'what',
  'when',
  'with',
  'from',
  'this',
  'that',
  'you',
  'your',
]);

function meaningfulTokens(query: string): string[] {
  return normalize(query)
    .split(' ')
    .filter(token => token.length > 1 && !QUERY_STOP_WORDS.has(token));
}

/** "how" and "how to" have no topic word left, so they mean the how-to list. */
export function isGuideBrowse(query: string): boolean {
  const normalized = normalize(query);
  return normalized.length > 0 && meaningfulTokens(normalized).length === 0;
}

export function searchCatalog(query: string, access: SearchAccess = {}): RankedSearchEntry[] {
  const visible = SEARCH_CATALOG.flatMap(entry => {
    if (!gatesAllow(entry, access)) return [];
    const hrefResolved = resolveHref(entry.href, access.mainOfficeId);
    if (entry.href && !hrefResolved) return [];
    return [{ ...entry, hrefResolved }];
  });

  const trimmed = query.trim();
  if (!trimmed) {
    return visible.filter(entry => entry.featured);
  }

  if (isGuideBrowse(trimmed)) {
    return visible
      .filter(entry => entry.kind === 'howto')
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  return visible
    .map(entry => ({ entry, score: entryScore(trimmed, entry) }))
    .filter(row => row.score > 0)
    .sort((a, b) => b.score - a.score || a.entry.title.localeCompare(b.entry.title))
    .map(row => row.entry);
}

export function groupResults(
  entries: RankedSearchEntry[]
): Array<{ group: string; entries: RankedSearchEntry[] }> {
  const order: string[] = [];
  const byGroup = new Map<string, RankedSearchEntry[]>();
  for (const entry of entries) {
    const list = byGroup.get(entry.group);
    if (list) list.push(entry);
    else {
      byGroup.set(entry.group, [entry]);
      order.push(entry.group);
    }
  }
  return order.map(group => ({ group, entries: byGroup.get(group) ?? [] }));
}
